import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { IntlShape } from 'react-intl';
import { Popover } from '../../../../global/popover/popover';
import Text from '../../../../global/text/text';
import { stringifyStaffMembers } from '../../../../../infrastructure/utils/users.utils';
import { DeliveryTeamMemberRole, IDeliveryItemDetailsDTO, IEntityRoleDTO } from '../../../../../api/events.dao';
import { MultiSelectButton } from '../../../../global/multi-select/multi-select-button';
import { DeliveryTeamList } from './delivery-team-list';
import { MultiSelectWithRoleList } from '../../../../global/multi-select-with-role.tsx/multi-select-with-role-list';
import MultiSelectWithRoleHeader from '../../../../global/multi-select-with-role.tsx/multi-select-with-role-header';
import { addText, cancelText } from '../../../../../constants/common-labels.constants';

export interface IDeliveryTeamButtonProps {
  removeDeliveryItem: (
      intl: IntlShape,
      deliveryMemberUserName: string,
      deliveryMemberUserId: string,
      eventId: number,
      accountId: number
  ) => Promise<void>;
  setDeliveryMemberRole: (
      intl: IntlShape,
      deliveryMemberUserName: string,
      deliveryMemberUserId: string,
      eventId: number,
      accountId: number,
      role: string
  ) => Promise<void>;
  setAssignedDeliveryTeam: (
      deliveryTeamMembers: IDeliveryItemDetailsDTO[],
      eventId: number,
      accountId: number
  ) => Promise<void>;
  handleSearchDeliveryTeamItems: (query: string) => Promise<void>;
  deliveryTeamItems: IDeliveryItemDetailsDTO[];
  sortedDeliveryTeamMembers: IDeliveryItemDetailsDTO[];
  canRenderAddMembersButton: boolean;
  intl: IntlShape;
  accountId: number;
  eventId: number;
  trackCancelDeliveryTeamButtonMixPanel?: () => void;
  onClose?: () => void;
  deliveryMemberRoles: IEntityRoleDTO[];
}

const DeliveryTeamButton = (props: IDeliveryTeamButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const roleButtonRef = useRef(null);
  const {
    canRenderAddMembersButton,
    handleSearchDeliveryTeamItems,
    sortedDeliveryTeamMembers,
    intl,
    removeDeliveryItem,
    accountId,
    eventId,
    deliveryTeamItems,
    setDeliveryMemberRole,
    setAssignedDeliveryTeam,
    trackCancelDeliveryTeamButtonMixPanel,
    onClose,
    deliveryMemberRoles,
  } = props;

  const formattedDeliveryMember = sortedDeliveryTeamMembers.map(item => ({
    fullName: `${item.firstName} ${item.lastName}`,
    ...item
  }));

  const handleClickOutside = e => {
    // prevent to close Popover when user clicks on MultiSelectButton(react-tiny-popover), which is outside of Popover
    if (!e.composedPath()
          .filter(elem => elem.tagName === 'DIV')
          .some(elem => elem.classList.value.includes('react-tiny-popover-container'))
    ) {
      setIsModalOpen(false);
      trackCancelDeliveryTeamButtonMixPanel();
    }
  };

  return (
    <div
        title={isModalOpen ? null : stringifyStaffMembers(sortedDeliveryTeamMembers).join('\n')}
        className={classnames('delivery-team-column', {
          'delivery-team-column--active': isModalOpen,
        })}
        ref={roleButtonRef}
    >
        <div className='delivery-team-column--wrapper' onClick={() => setIsModalOpen(prev => !prev)}>
          <Text
              message={`${sortedDeliveryTeamMembers[0].firstName} ${sortedDeliveryTeamMembers[0].lastName}`}
              withEllipsis
              withoutTitle
              tag={
                  sortedDeliveryTeamMembers[0].deliveryTeamMemberRole ===
                  DeliveryTeamMemberRole.PROJECT_MANAGER
                      ? 'b'
                      : 'span'
              }
          />
          {sortedDeliveryTeamMembers.length > 1 && (
              <span className='delivery-team-column__attendees-count'>
                  {`+${sortedDeliveryTeamMembers.length - 1}`}
              </span>
          )}
        </div>
        <Popover
            padding={0}
            isOpen={isModalOpen}
            buttonRef={roleButtonRef}
            customWidth={343}
            customLeft={0}
            withCloseIcon
            onClose={() => {
                setIsModalOpen(false);
                trackCancelDeliveryTeamButtonMixPanel();
            }}
            onClickOutside={e => handleClickOutside(e)}
        >
          <DeliveryTeamList
            deliveryTeamItems={sortedDeliveryTeamMembers}
            removeDeliveryItem={canRenderAddMembersButton ? removeDeliveryItem : null}
            setDeliveryMemberRole={canRenderAddMembersButton ? setDeliveryMemberRole : null}
            intl={intl}
            eventId={eventId}
            accountId={accountId}
            canRenderAddMembersButton={canRenderAddMembersButton}
          />
          { canRenderAddMembersButton && <div className='add-button-wrapper'>
            <MultiSelectButton
              text={'+ Add Delivery Team'}
              className='add-attendee-btn'
              items={deliveryTeamItems}
              disabled={formattedDeliveryMember}
              value={formattedDeliveryMember}
              keyProperty='username'
              nameProperty='fullName'
              applyButtonText={addText()}
              resetButtonText={cancelText()}
              popoverClassName={'multi-select-with-role event_form__items-section m-bottom-0px'}
              renderHeader={MultiSelectWithRoleHeader}
              renderSelectList={rest => <MultiSelectWithRoleList
                withSearchInput
                withUnassigned
                entityRoleKey='deliveryTeamMemberRole'
                intlRoleKey='deliveryTeamMemberRole'
                entityRolesList={deliveryMemberRoles}
                {...rest}
                intl={intl}
              />}
              filterHandler={handleSearchDeliveryTeamItems}
              searchWithDebounce
              onChange={teamMembers => setAssignedDeliveryTeam(teamMembers, eventId, accountId)}
              onCancel={trackCancelDeliveryTeamButtonMixPanel}
              onClose={onClose}
            />

          </div> }
        </Popover>
    </div>
  );
};

export default DeliveryTeamButton;
